import { buildTeacherAcademyDashboard } from './content/buildAcademyDashboard';
import { isAcademyConfirmedBy } from '../../../components/academy/Utilities/academyProgress';

export type AcademyConfirmedBy = 'none' | 'self' | 'system';
export type AcademySkillPart = 'learn' | 'apply';
export type AcademyConfirmationPolicy =
  | 'user_full_confirm'
  | 'confirm_on_warning'
  | 'block_on_warning';

export interface AcademySkillCheckResult {
  skillKey: string;
  planToken: string;
  part: AcademySkillPart;
  confirmedBy: AcademyConfirmedBy;
  teacherConfirmationAllowed: boolean;
  checkItems: AcademySkillCheckItem[];
}

export interface AcademySkillCheckItem {
  label: string;
  passed: boolean;
}

export interface AcademyMetric {
  metricKey: string;
  label: string;
  value: number;
}

export interface AcademyTier {
  tierKey: string;
  nextTierKey?: string | null;
  step: number;
  name: string;
  description: string;
  metrics?: AcademyMetric[];
}

export type AcademyPathKey = 'plan_prepare' | 'teach_facilitate' | 'assess_adjust';
export type AcademyLearnContentType = 'video' | 'interactive_video' | 'quick_tour' | 'text';

export interface AcademyLearnResource {
  label: string;
  url: string;
}

export interface AcademyLearnSection {
  title: string;
  body: string[];
}

export interface AcademyLearnContentDetails {
  intro: string;
  referenceText?: string[];
  goals?: string[];
  practice?: string[];
  sections?: AcademyLearnSection[];
  resources?: AcademyLearnResource[];
  timingNote?: string | null;
  supportNote?: string | null;
  checkPrompt?: string;
  transcript?: string[];
}

export interface AcademyLearnContent {
  contentType: AcademyLearnContentType;
  title: string;
  contentPath: string;
  sourceUrl: string | null;
  body: string;
  estimatedMinutes: number;
  details: AcademyLearnContentDetails;
}

export interface AcademyLesson {
  id: string;
  skillKey: string;
  title: string;
  description: string;
  pathKey: AcademyPathKey;
  learnType: AcademyLearnContentType;
  tierKeys: string[];
  displayOrder: number;
  recommendedOrder: number;
  learnConfirmedBy: AcademyConfirmedBy;
  applyConfirmedBy: AcademyConfirmedBy;
  learnCompletedAt: string | null;
  applyCompletedAt: string | null;
  learnContent: AcademyLearnContent;
  learnRequired: boolean;
  applyRequired: boolean;
  confirmationPolicy?: AcademyConfirmationPolicy;
  appliedActions: string[];
  selfReportPrompt: string | null;
}

export type AcademySkill = AcademyLesson;

export interface AcademySkillPath {
  id: AcademyPathKey;
  pathKey: AcademyPathKey;
  title: string;
  displayOrder: number;
  skills: AcademySkill[];
}

export interface AcademyArea {
  academyAreaKey: string;
  planToken: string;
  planName?: string;
  label: string;
  heading: string;
  description: string;
  metrics: AcademyMetric[];
  tiers: AcademyTier[];
  recommendedLessons: AcademyLesson[];
  skillPaths: AcademySkillPath[];
}

export interface TeacherAcademyDashboard {
  hasAcademyPlans: boolean;
  defaultAcademyAreaKey: string;
  academyAreas: AcademyArea[];
}

export interface AcademyPlanSkillState {
  skillKey: string;
  skillPath: AcademyPathKey;
  displayOrder: number;
  recommendedOrder: number;
  learnRequired: boolean;
  applyRequired: boolean;
  learnConfirmedBy: AcademyConfirmedBy;
  applyConfirmedBy: AcademyConfirmedBy;
  learnCompletedAt: string | null;
  applyCompletedAt: string | null;
}

export interface AcademyPlanTierState {
  tierKey: string;
  nextTierKey: string | null;
  skills: AcademyPlanSkillState[];
}

export interface AcademyPlanState {
  planToken: string;
  planName: string;
  metricValues?: Array<{ metricKey: string; value: number }>;
  tiers: AcademyPlanTierState[];
}

export interface TeacherAcademyDashboardResponse {
  plans: AcademyPlanState[];
}

const API_SERVER = process.env.API_SERVER || '';
const ACADEMY_PATH_KEYS = new Set<AcademyPathKey>([
  'plan_prepare',
  'teach_facilitate',
  'assess_adjust',
]);

export class AcademyAccessDeniedError extends Error {
  constructor() {
    super('Academy is not available for this user.');
    this.name = 'AcademyAccessDeniedError';
  }
}

export async function getTeacherAcademyDashboard(): Promise<TeacherAcademyDashboard> {
  const response = await fetch(joinUrl(API_SERVER, 'academy/teacher'), {
    credentials: 'include',
  });

  if (!response.ok) {
    if (response.status === 403) {
      throw new AcademyAccessDeniedError();
    }

    throw new Error(getAcademyRequestErrorMessage(response.status));
  }

  return buildTeacherAcademyDashboard(parseTeacherAcademyDashboardResponse(await response.json()));
}

export async function checkTeacherAcademySkill(
  skillKey: string,
  request: {
    planToken: string;
    part: AcademySkillPart;
    confirmedBy?: 'self';
  },
): Promise<AcademySkillCheckResult> {
  const response = await fetch(
    joinUrl(API_SERVER, `academy/teacher/skills/${encodeURIComponent(skillKey)}/check`),
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'content-type': 'application/json',
      },
      body: JSON.stringify(request),
    },
  );

  if (!response.ok) {
    if (response.status === 403) {
      throw new AcademyAccessDeniedError();
    }

    throw new Error(getAcademyRequestErrorMessage(response.status));
  }

  return (await response.json()) as AcademySkillCheckResult;
}

function joinUrl(baseUrl: string, path: string): string {
  return `${baseUrl.replace(/\/$/, '')}/${path.replace(/^\//, '')}`;
}

function getAcademyRequestErrorMessage(status: number): string {
  return `Academy request failed with status ${status}`;
}

function parseTeacherAcademyDashboardResponse(value: unknown): TeacherAcademyDashboardResponse {
  if (!isObjectRecord(value) || !Array.isArray(value.plans)) {
    throw new Error('Academy response has invalid plans.');
  }

  value.plans.forEach((plan) => {
    if (
      !isObjectRecord(plan) ||
      !isString(plan.planToken) ||
      !isString(plan.planName) ||
      !(plan.metricValues === undefined || Array.isArray(plan.metricValues)) ||
      !Array.isArray(plan.tiers)
    ) {
      throw new Error('Academy response has invalid plan data.');
    }

    if (Array.isArray(plan.metricValues)) {
      plan.metricValues.forEach((metricValue) => {
        if (
          !isObjectRecord(metricValue) ||
          !isString(metricValue.metricKey) ||
          !isFiniteNumber(metricValue.value)
        ) {
          throw new Error('Academy response has invalid metric data.');
        }
      });
    }

    plan.tiers.forEach((tier) => {
      if (
        !isObjectRecord(tier) ||
        !isString(tier.tierKey) ||
        !(tier.nextTierKey === null || isString(tier.nextTierKey)) ||
        !Array.isArray(tier.skills)
      ) {
        throw new Error('Academy response has invalid tier data.');
      }

      tier.skills.forEach((skill) => {
        if (
          !isObjectRecord(skill) ||
          !isString(skill.skillKey) ||
          !isAcademyPathKey(skill.skillPath) ||
          !isFiniteNumber(skill.displayOrder) ||
          !isFiniteNumber(skill.recommendedOrder) ||
          typeof skill.learnRequired !== 'boolean' ||
          typeof skill.applyRequired !== 'boolean' ||
          !isAcademyConfirmedBy(skill.learnConfirmedBy) ||
          !isAcademyConfirmedBy(skill.applyConfirmedBy) ||
          !isDateTextOrNull(skill.learnCompletedAt) ||
          !isDateTextOrNull(skill.applyCompletedAt)
        ) {
          throw new Error('Academy response has invalid skill data.');
        }
      });
    });
  });

  return value as unknown as TeacherAcademyDashboardResponse;
}

function isObjectRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isString(value: unknown): value is string {
  return typeof value === 'string';
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function isDateTextOrNull(value: unknown): value is string | null {
  return value === null || isString(value);
}

function isAcademyPathKey(value: unknown): value is AcademyPathKey {
  return isString(value) && ACADEMY_PATH_KEYS.has(value as AcademyPathKey);
}
